import { IDriverset } from "../api/models";
import { IRect } from "./IRect";

/**
 * The four simulation choices the user can pick in the Simulation tab. Each
 * corresponds to one ORGANIX backend invocation; the modelCode passed to the
 * API is derived at call time via modelCodeFor() — kept off IArea so that the
 * area model isn't coupled to ORGANIX's internal numbering.
 */
type TSimulationKind = 'deposition' | 'depositionDiagenix' | 'dispersion' | 'pathogens';

/**
 * ORGANIX modelCode for a given simulation kind. The codes are ORGANIX's
 * FINSEXPLICITEULER* constants (CODMotorganix.h): 3 is chlorophyll, not
 * pathogens.
 */
const modelCodeFor = (kind: TSimulationKind): number => {
  switch(kind) {
    case 'deposition':
    case 'depositionDiagenix': return 0;
    case 'dispersion':         return 2;
    case 'pathogens':          return 4;
  }
}

/** Whether the run also invokes Diagenix alongside ORGANIX. */
const usesDiagenix = (kind: TSimulationKind): boolean => kind === 'depositionDiagenix';

/**
 * Whether the run is a deposition model, i.e. one concerned with particles
 * sinking to the seabed. Both plain deposition and diagenesis qualify.
 */
const isDeposition = (kind: TSimulationKind): boolean =>
  kind === 'deposition' || kind === 'depositionDiagenix';

/**
 * The months a culture period may start in. `day` is the 0-based day-of-year the
 * month begins on in the fixed 365-day year the driver sets use, so January is 0,
 * not 1 — it lines up with AquaFish's AStartTime and with the interpolation index
 * returned by interpolateDriverSet, both of which put 1 January at 0.
 */
interface ICultureStartMonth {
  /** 1 = January. */
  month: number;
  name: string;
  /** 0-based day-of-year the month begins on. */
  day: number;
}

const CULTURE_START_MONTHS: ICultureStartMonth[] = [
  { month:  1, name: 'January',   day:   0 },
  { month:  2, name: 'February',  day:  31 },
  { month:  3, name: 'March',     day:  59 },
  { month:  4, name: 'April',     day:  90 },
  { month:  5, name: 'May',       day: 120 },
  { month:  6, name: 'June',      day: 151 },
  { month:  7, name: 'July',      day: 181 },
  { month:  8, name: 'August',    day: 212 },
  { month:  9, name: 'September', day: 243 },
  { month: 10, name: 'October',   day: 273 },
  { month: 11, name: 'November',  day: 304 },
  { month: 12, name: 'December',  day: 334 },
];

/**
 * The 0-based day-of-year a culture period starts on. An unset startMonth means
 * January, which is what every project assumed before the field existed — so old
 * .fins files need no migration. An out-of-range month falls back to January
 * rather than throwing, since it can only come from a hand-edited file.
 */
const startDayFor = (startMonth?: number | null): number =>
  CULTURE_START_MONTHS.find(m => m.month === startMonth)?.day ?? 0;

interface IArea {
  name: string;
  rect: IRect;
  resolution: number;
  bathymetry: 'positive' | 'negative';
  demThreshold: number;

  days: number;
  structureDepth: number;
  faecesDiameter: number;
  feedPelletDiameter: number;
  xDispersion: number;
  yDispersion: number;
  solver: number;
  timestepReductionFactor: number;
  simulationKind: TSimulationKind;
  // Pathogen model inputs, sent to ORGANIX only for pathogen runs. The emission
  // period (days) replaces `days` as cultureDays: it is weeks, not a culture
  // cycle, so it has its own 1-30 control. Die-off constant in d-1 (0 = no
  // die-off). Vertical dispersion has no UI yet and stays true.
  pathogenEmissionPeriod: number;
  pathogenDecay: number;
  pathogenVerticalDispersion: boolean;
  // Month the culture period starts in, 1 = January. Absent on areas saved
  // before the field existed; absent and null both mean January. Read it
  // through startDayFor(), never directly.
  startMonth?: number | null;

  // Per-area driver set. Snapshot copied from a system driver set or hand-edited
  // by an admin. Null only on areas created without any system loaded and not
  // yet edited by an admin — such an area cannot be simulated.
  driverset: IDriverset | null;
}

export { IArea, TSimulationKind, modelCodeFor, usesDiagenix, isDeposition, ICultureStartMonth, CULTURE_START_MONTHS, startDayFor }
